import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChefHat,
  CircleAlert,
  Clock3,
  Download,
  Hammer,
  House,
  LayoutPanelTop,
  Lightbulb,
  MoreHorizontal,
  PanelTop,
  Sparkles,
  UserCheck,
} from "lucide-react";

import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { brand, pageTitle } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const onePagerUrl = `${brand.url}one-pager`;
const onePagerDescription =
  "See how Qotta turns uncertain website visitors into informed buyers with AI-guided discovery and personalized price estimates.";

export const Route = createFileRoute("/one-pager")({
  head: () => ({
    meta: [
      { title: pageTitle("Product One Pager") },
      {
        name: "description",
        content: onePagerDescription,
      },
      { property: "og:title", content: pageTitle("Product One Pager") },
      { property: "og:description", content: onePagerDescription },
      { property: "og:url", content: onePagerUrl },
      { property: "og:image", content: brand.socialImageUrl },
      { property: "og:image:alt", content: brand.socialImageAlt },
      { name: "twitter:title", content: pageTitle("Product One Pager") },
      { name: "twitter:description", content: onePagerDescription },
      { name: "twitter:image", content: brand.socialImageUrl },
      { name: "twitter:image:alt", content: brand.socialImageAlt },
    ],
    links: [{ rel: "canonical", href: onePagerUrl }],
  }),
  component: OnePager,
});

const kitchenChoices = [
  { key: "new", icon: ChefHat },
  { key: "country", icon: House },
  { key: "modern", icon: PanelTop },
  { key: "renovation", icon: Hammer },
  { key: "island", icon: LayoutPanelTop },
  { key: "other", icon: MoreHorizontal },
] as const;

const highlights = [
  { key: "conversion", icon: ArrowUpRight },
  { key: "leads", icon: UserCheck },
  { key: "time", icon: Clock3 },
] as const;

const opportunityCards = [
  { key: "problem", icon: CircleAlert, tone: "rose" },
  { key: "opportunity", icon: Lightbulb, tone: "teal" },
  { key: "solution", icon: Sparkles, tone: "blue" },
] as const;

const steps = [
  "domain",
  "cards",
  "understand",
  "questions",
  "estimate",
  "lead",
] as const;

const benefits = {
  customer: ["certainty", "range", "simple", "decision"],
  business: ["leads", "conversion", "time", "cycle"],
} as const;

function OnePager() {
  const { t, dir } = useI18n();
  const DirectionArrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  return (
    <div className="one-pager-shell min-h-screen overflow-hidden bg-background text-start">
      <div className="hero-gradient pointer-events-none absolute inset-x-0 top-0 h-[780px]" />
      <div className="grid-lines pointer-events-none absolute inset-x-0 top-0 h-[620px] opacity-45" />

      <header className="relative z-20 mx-auto flex max-w-7xl items-center gap-3 px-5 py-6 sm:px-8 lg:px-10">
        <Link to="/" aria-label={t("onePager.nav.home")}>
          <Logo size="large" />
        </Link>
        <div className="one-pager-controls ms-auto flex items-center gap-2">
          <Button
            type="button"
            aria-label={t("onePager.download")}
            className="h-10 rounded-full px-4 shadow-md"
            onClick={() => window.print()}
          >
            <Download className="size-4" aria-hidden />
            <span className="hidden sm:inline">{t("onePager.download")}</span>
          </Button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-5 pb-20 sm:px-8 lg:px-10">
        <section className="grid min-h-[620px] items-center gap-12 py-12 lg:grid-cols-[0.88fr_1.12fr] lg:gap-16 lg:py-20">
          <div className="animate-fade-up max-w-2xl">
            <Logo size="hero" className="mb-8" />
            <h1 className="mt-7 text-4xl leading-[1.08] font-extrabold sm:text-5xl lg:text-[4.15rem]">
              {t("onePager.hero.title")}
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed font-semibold text-foreground/80 sm:text-xl">
              {t("onePager.hero.subheadline")}
            </p>
            <p className="mt-4 max-w-xl text-base leading-8 text-muted-foreground">
              {t("onePager.hero.body")}
            </p>
          </div>

          <ProductMockup />
        </section>

        <section aria-labelledby="highlights-title" className="py-14 sm:py-20">
          <SectionHeading
            id="highlights-title"
            eyebrow={t("onePager.highlights.eyebrow")}
            title={t("onePager.highlights.title")}
          />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {highlights.map(({ key, icon: Icon }, index) => (
              <article
                key={key}
                className="surface lift relative overflow-hidden p-7 sm:p-8"
              >
                <span className="inline-grid size-12 place-items-center rounded-2xl bg-primary-soft text-primary">
                  <Icon className="size-5" aria-hidden />
                </span>
                <p className="num mt-8 text-xs font-bold tracking-[0.18em] text-muted-foreground rtl:tracking-normal">
                  0{index + 1}
                </p>
                <h3 className="mt-2 text-xl font-extrabold">
                  {t(`onePager.highlight.${key}`)}
                </h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  {t(`onePager.highlight.${key}.body`)}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="opportunity-title" className="py-14 sm:py-20">
          <SectionHeading
            id="opportunity-title"
            eyebrow={t("onePager.shift.eyebrow")}
            title={t("onePager.shift.title")}
          />
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {opportunityCards.map(({ key, icon: Icon, tone }) => (
              <article
                key={key}
                className={cn(
                  "surface-elevated relative overflow-hidden p-7 sm:p-9",
                  tone === "blue" && "intelligence-panel",
                )}
              >
                <span
                  className={cn(
                    "inline-grid size-11 place-items-center rounded-2xl",
                    tone === "rose" && "bg-destructive/10 text-destructive",
                    tone === "teal" && "bg-teal-soft text-teal",
                    tone === "blue" && "bg-primary-soft text-primary",
                  )}
                >
                  <Icon className="size-5" aria-hidden />
                </span>
                <h3 className="mt-7 text-2xl font-extrabold">
                  {t(`onePager.${key}.title`)}
                </h3>
                <p className="mt-4 text-[15px] leading-8 text-muted-foreground">
                  {t(`onePager.${key}.body`)}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="how-title" className="py-14 sm:py-20">
          <SectionHeading
            id="how-title"
            eyebrow={t("onePager.how.eyebrow")}
            title={t("onePager.how.title")}
          />
          <div className="surface-elevated relative mt-10 overflow-hidden p-6 sm:p-9 lg:p-12">
            <div className="absolute inset-x-16 top-[4.45rem] hidden h-px bg-border lg:block" />
            <ol className="relative grid gap-4 lg:grid-cols-6 lg:gap-3">
              {steps.map((step, index) => (
                <li
                  key={step}
                  className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 lg:flex-col lg:items-start lg:border-0 lg:bg-transparent lg:p-0 lg:text-start"
                >
                  <span className="num grid size-11 shrink-0 place-items-center rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground shadow-md ring-8 ring-card">
                    {index + 1}
                  </span>
                  <p className="text-sm leading-6 font-bold lg:mt-5">
                    {t(`onePager.step.${step}`)}
                  </p>
                  {index < steps.length - 1 && (
                    <DirectionArrow
                      className="ms-auto size-4 shrink-0 text-primary/45 lg:hidden"
                      aria-hidden
                    />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section aria-labelledby="benefits-title" className="py-14 sm:py-20">
          <SectionHeading
            id="benefits-title"
            eyebrow={t("onePager.benefits.eyebrow")}
            title={t("onePager.benefits.title")}
          />
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            <BenefitCard type="customer" items={benefits.customer} />
            <BenefitCard type="business" items={benefits.business} primary />
          </div>
        </section>

        <section className="py-14 sm:py-20" aria-labelledby="why-now-title">
          <div className="intelligence-panel relative overflow-hidden p-8 sm:p-12 lg:p-16">
            <div className="pointer-events-none absolute -end-24 -top-24 size-72 rounded-full bg-primary-soft/70 blur-3xl" />
            <div className="relative max-w-3xl">
              <span className="discovery-badge px-3.5 py-2 text-xs font-bold">
                {t("onePager.whyNow.eyebrow")}
              </span>
              <h2
                id="why-now-title"
                className="mt-6 text-3xl font-extrabold sm:text-5xl"
              >
                {t("onePager.whyNow.title")}
              </h2>
              <p className="mt-7 text-lg leading-9 font-semibold text-foreground/80">
                {t("onePager.whyNow.body1")}
              </p>
              <p className="mt-3 text-base leading-8 text-muted-foreground">
                {t("onePager.whyNow.body2")}
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="one-pager-footer relative overflow-hidden bg-primary text-primary-foreground">
        <div className="mesh-gradient absolute inset-0 opacity-70" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-10 px-5 py-14 sm:px-8 md:flex-row md:items-center lg:px-10 lg:py-16">
          <div>
            <Logo size="hero" />
            <a
              href={brand.url}
              className="mt-4 block text-lg font-semibold text-primary-foreground/75 transition-colors hover:text-primary-foreground"
            >
              {brand.domain}
            </a>
          </div>
          <p className="max-w-md text-lg leading-8 font-medium text-primary-foreground/80 md:mx-auto">
            {t("onePager.footer.tagline")}
          </p>
          <QrPlaceholder label={t("onePager.footer.qrLabel")} />
        </div>
      </footer>
    </div>
  );
}

function ProductMockup() {
  const { t } = useI18n();

  return (
    <div className="animate-slide-in relative mx-auto w-full max-w-[680px] pb-16 sm:pb-20">
      <div className="surface-elevated relative overflow-hidden p-5 sm:p-7 lg:pe-28">
        <div className="flex items-center gap-2" aria-hidden>
          <span className="size-2.5 rounded-full bg-primary" />
          <span className="size-2.5 rounded-full bg-border-strong" />
          <span className="size-2.5 rounded-full bg-border" />
          <div className="ms-2 h-1 flex-1 overflow-hidden rounded-full bg-secondary">
            <div className="discovery-progress h-full w-[38%] rounded-full" />
          </div>
        </div>
        <p className="mt-7 text-lg font-extrabold sm:text-xl">
          {t("onePager.mockup.question")}
        </p>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t("onePager.mockup.hint")}
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {kitchenChoices.map(({ key, icon: Icon }, index) => (
            <div
              key={key}
              className={cn(
                "rounded-2xl border bg-card p-3 shadow-sm",
                index === 1 &&
                  "border-primary bg-primary-soft/60 ring-2 ring-primary/10",
              )}
            >
              <span
                className={cn(
                  "grid size-9 place-items-center rounded-xl bg-secondary text-primary",
                  index === 1 && "bg-primary text-primary-foreground",
                )}
              >
                <Icon className="size-4" aria-hidden />
              </span>
              <p className="mt-3 text-xs leading-5 font-bold">
                {t(`onePager.mockup.choice.${key}`)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute -bottom-2 end-0 w-[44%] min-w-[190px] max-w-[250px] rotate-[-2deg] rounded-[2.6rem] border-[7px] border-foreground bg-card p-2 shadow-2xl rtl:rotate-[2deg]">
        <div className="overflow-hidden rounded-[2rem] bg-background px-4 pb-6 pt-3 text-center">
          <div className="mx-auto h-1.5 w-14 rounded-full bg-foreground/80" />
          <Logo size="small" className="mt-7" />
          <p className="mt-10 text-xs font-bold text-muted-foreground">
            {t("onePager.mockup.resultTitle")}
          </p>
          <p
            className="num mt-3 whitespace-nowrap text-xl font-extrabold text-primary sm:text-2xl"
            dir="ltr"
          >
            {t("onePager.mockup.price")}
          </p>
          <p className="mt-2 text-[10px] text-muted-foreground">
            {t("onePager.mockup.resultNote")}
          </p>
          <div className="mt-8 space-y-2 text-start">
            {["planning", "materials", "installation"].map((item) => (
              <p
                key={item}
                className="flex items-center gap-2 text-[10px] font-semibold"
              >
                <Check className="size-3 text-teal" aria-hidden />
                {t(`onePager.mockup.includes.${item}`)}
              </p>
            ))}
          </div>
          <div className="mt-7 rounded-full bg-primary px-3 py-2.5 text-[10px] font-bold text-primary-foreground">
            {t("onePager.mockup.cta")}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({
  id,
  eyebrow,
  title,
}: {
  id: string;
  eyebrow: string;
  title: string;
}) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-extrabold tracking-[0.18em] text-primary uppercase rtl:tracking-normal rtl:normal-case">
        {eyebrow}
      </p>
      <h2 id={id} className="mt-4 text-3xl font-extrabold sm:text-5xl">
        {title}
      </h2>
    </div>
  );
}

function BenefitCard({
  type,
  items,
  primary,
}: {
  type: "customer" | "business";
  items: readonly string[];
  primary?: boolean;
}) {
  const { t } = useI18n();

  return (
    <article
      className={cn("surface p-7 sm:p-10", primary && "discovery-panel")}
    >
      <p className="text-xs font-extrabold tracking-[0.16em] text-primary uppercase rtl:tracking-normal rtl:normal-case">
        {t(`onePager.benefit.${type}.eyebrow`)}
      </p>
      <h3 className="mt-3 text-2xl font-extrabold sm:text-3xl">
        {t(`onePager.benefit.${type}.title`)}
      </h3>
      <ul className="mt-7 grid gap-3 sm:grid-cols-2">
        {items.map((item) => (
          <li
            key={item}
            className="flex items-center gap-3 text-sm font-semibold"
          >
            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-success-soft text-success">
              <Check className="size-3.5" aria-hidden />
            </span>
            {t(`onePager.benefit.${type}.${item}`)}
          </li>
        ))}
      </ul>
    </article>
  );
}

const qrPattern = [
  "111010111",
  "101000101",
  "111011111",
  "000110000",
  "101011101",
  "011101010",
  "111010111",
  "101110101",
  "111011111",
];

function QrPlaceholder({ label }: { label: string }) {
  return (
    <div
      className="grid size-28 shrink-0 grid-cols-9 gap-[2px] rounded-2xl bg-card p-3 shadow-xl"
      role="img"
      aria-label={label}
    >
      {qrPattern
        .join("")
        .split("")
        .map((cell, index) => (
          <span
            key={index}
            className={
              cell === "1" ? "rounded-[1px] bg-primary" : "bg-transparent"
            }
          />
        ))}
    </div>
  );
}
